import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import ResultCard from '../ResultCard';

// Loading Skeleton Card
const SkeletonCard = () => (
  <Card sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, borderRadius: 2 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        p: { xs: 2, sm: 3 },
        minWidth: { xs: 'auto', sm: 120 },
      }}
    >
      <Skeleton variant="circular" width={64} height={64} />
    </Box>
    <CardContent sx={{ flexGrow: 1, p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
        <Skeleton variant="text" sx={{ fontSize: '1.5rem' }} width="60%" />
        <Skeleton variant="rounded" width={100} height={24} />
      </Box>
      <Box display="flex" gap={3} marginBottom={1} flexWrap="wrap">
        <Skeleton variant="text" width={120} />
        <Skeleton variant="text" width={140} />
        <Skeleton variant="text" width={100} />
      </Box>
      <Skeleton variant="text" width="80%" />
    </CardContent>
  </Card>
);

const ResultsGrid = ({
  organizations,
  dealers,
  total,
  hasMore,
  onLoadMore,
  loadingMore,
  loading,
}) => {
  const theme = useTheme();

  // Combine results with type indicator
  const allResults = [
    ...organizations.map(org => ({ ...org, _type: 'organization' })),
    ...dealers.map(dealer => ({ ...dealer, _type: 'dealer' })),
  ];

  if (loading) {
    return (
      <Box paddingY={2}>
        <Skeleton variant="text" width={200} sx={{ fontSize: '1.25rem', marginBottom: 3 }} />
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} key={index}>
              <SkeletonCard />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box paddingY={2}>
      {/* Results Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={3}
      >
        <Typography variant="h5" fontWeight={600}>
          {total} {total === 1 ? 'Result' : 'Results'} Found
        </Typography>
        <Box display="flex" gap={2}>
          {organizations.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {organizations.length} Driving Schools
            </Typography>
          )}
          {dealers.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {dealers.length} Car Dealers
            </Typography>
          )}
        </Box>
      </Box>

      {/* Results Grid */}
      <Grid container spacing={3}>
        {allResults.map((item) => (
          <Grid item xs={12} key={`${item._type}-${item._id}`}>
            <ResultCard data={item} type={item._type} />
          </Grid>
        ))}
      </Grid>

      {/* Load More Button */}
      {hasMore && (
        <Box display="flex" justifyContent="center" marginTop={4}>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={onLoadMore}
            disabled={loadingMore}
            sx={{
              px: 6,
              py: 1.5,
              borderRadius: 2,
            }}
          >
            {loadingMore ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

ResultsGrid.propTypes = {
  organizations: PropTypes.array,
  dealers: PropTypes.array,
  total: PropTypes.number,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  loadingMore: PropTypes.bool,
  loading: PropTypes.bool,
};

ResultsGrid.defaultProps = {
  organizations: [],
  dealers: [],
  total: 0,
  hasMore: false,
  onLoadMore: () => {},
  loadingMore: false,
  loading: false,
};

export default ResultsGrid;
